
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useOnlineStatus } from '@/hooks/use-online-status';

export function useOfflineSync() {
  const [isSyncing, setIsSyncing] = useState(false);
  const { toast } = useToast();
  const isOnline = useOnlineStatus();

  const syncOfflineData = async () => {
    if (!isOnline || isSyncing) return;

    try {
      setIsSyncing(true);

      // Hent offline ændringer der ikke er synkroniseret endnu
      const { data: offlineChanges, error: fetchError } = await supabase
        .from('offline_sync')
        .select('*')
        .is('synced_at', null)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      if (!offlineChanges || offlineChanges.length === 0) {
        return;
      }

      // Behandl hver offline ændring
      for (const change of offlineChanges) {
        try {
          switch (change.operation) {
            case 'create':
              await supabase
                .from('tasks')
                .insert(change.data)
                .select()
                .single();
              break;
            case 'update':
              await supabase
                .from('tasks')
                .update(change.data)
                .eq('id', change.task_id);
              break;
            case 'delete':
              await supabase
                .from('tasks')
                .delete()
                .eq('id', change.task_id);
              break;
          }

          // Marker ændringen som synkroniseret
          await supabase
            .from('offline_sync')
            .update({ synced_at: new Date().toISOString() })
            .eq('id', change.id);

        } catch (error: any) {
          console.error('Fejl ved synkronisering af offline ændring:', error);
          await supabase
            .from('offline_sync')
            .update({ 
              sync_error: error.message || 'Ukendt fejl ved synkronisering'
            })
            .eq('id', change.id);
        }
      }

      toast({
        title: "Synkronisering gennemført",
        description: `${offlineChanges.length} offline ændringer er blevet synkroniseret.`
      });

    } catch (error: any) {
      console.error('Fejl ved offline synkronisering:', error);
      toast({
        title: "Synkroniseringsfejl",
        description: "Der opstod en fejl under synkronisering af offline data.",
        variant: "destructive"
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Automatisk synkronisering når der er internetforbindelse
  useEffect(() => {
    if (isOnline) {
      syncOfflineData();
    }
  }, [isOnline]);

  return {
    isSyncing,
    syncOfflineData
  };
}
